/**
 * Lazy visual-search entry — chunk loads only after camera button press (not on Home mount).
 *
 * STAGING-LAUNCH-READINESS-002-R2 — first-tap: onPointerUp + onClick fallback.
 * R7.2.2F1 — camera chunk load failure must never blank the Mini App.
 * R7.2.2F4 — SVG CustomerCameraTrigger (no emoji glyph / mojibake).
 */
import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { CustomerCameraTrigger } from "./CustomerCameraTrigger.js";

const VisualSearchControl = lazy(async () => {
  const m = await import("./VisualSearchControl.js");
  return { default: m.VisualSearchControl };
});

const API_BASE = ""; // same-origin Pages Functions (avoids Telegram WebView CORS to Worker)

const ARM_GUARD_MS = 450;

class VisualSearchChunkBoundary extends Component<
  {
    lang: StoreV2Lang;
    onRetry: () => void;
    onChoosePhoto: () => void;
    children: ReactNode;
  },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render() {
    if (this.state.failed) {
      const uk = this.props.lang !== "en";
      return (
        <div className="sv2-visual-search-chunk-fail" data-testid="sv2-visual-search-chunk-fail" role="alert">
          <p>{uk ? "Не вдалося відкрити камеру." : "Could not open the camera."}</p>
          <div className="sv2-visual-search-chunk-fail-actions">
            <button type="button" className="sv2-cta sv2-secondary" onClick={this.props.onChoosePhoto}>
              {uk ? "Обрати фото" : "Choose a photo"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              onClick={() => {
                this.setState({ failed: false });
                this.props.onRetry();
              }}
            >
              {uk ? "Спробувати ще раз" : "Try again"}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function LazyVisualSearchSlot(props: {
  lang: StoreV2Lang;
  sessionKey: string;
  onOpenProduct: (
    productId: string,
    seed?: CatalogV2Product | { id?: string; title?: string; imageUrl?: string | null; priceUah?: number | null },
  ) => void;
  onAddToCart?: (
    productId: string,
    seed?: {
      title?: string;
      imageUrl?: string | null;
      priceUah?: number | null;
      sku?: string | null;
      barcode?: string | null;
    },
  ) => void;
  onOpenCart?: () => void;
  qtyOf?: (productId: string) => number;
  emit: (name: string, payload: Record<string, unknown>) => void;
  resetKey?: string;
}) {
  const [armed, setArmed] = useState(false);
  const [chunkKey, setChunkKey] = useState(0);
  const mountStart = useRef(0);
  const lastArmRef = useRef(0);
  const galleryRef = useRef<HTMLInputElement>(null);

  const arm = useCallback(() => {
    const now = performance.now();
    if (now - lastArmRef.current < ARM_GUARD_MS) return;
    lastArmRef.current = now;
    mountStart.current = performance.now();
    setArmed(true);
    props.emit("visual_panel_mount_ms", { ms: 0, phase: "start" });
  }, [props.emit]);

  const onArmPointerUp = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      arm();
    },
    [arm],
  );

  const onArmClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (e.nativeEvent instanceof globalThis.PointerEvent && e.nativeEvent.pointerType === "touch") return;
      e.stopPropagation();
      arm();
    },
    [arm],
  );

  const onArmKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      e.stopPropagation();
      arm();
    },
    [arm],
  );

  if (!armed) {
    return (
      <CustomerCameraTrigger
        lang={props.lang === "en" ? "en" : "uk"}
        state="idle"
        onPointerUp={onArmPointerUp}
        onClick={onArmClick}
        onKeyDown={onArmKeyDown}
      />
    );
  }

  return (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        hidden
        data-testid="sv2-visual-search-chunk-gallery"
        onChange={() => {
          setChunkKey((k) => k + 1);
          props.emit("visual_search_chunk_gallery_escape", {});
        }}
      />
      <VisualSearchChunkBoundary
        lang={props.lang}
        onRetry={() => {
          setChunkKey((k) => k + 1);
          props.emit("visual_search_chunk_retry", {});
        }}
        onChoosePhoto={() => galleryRef.current?.click()}
      >
        <Suspense
          fallback={
            <CustomerCameraTrigger lang={props.lang === "en" ? "en" : "uk"} state="loading" disabled />
          }
        >
          <VisualSearchControl
            key={chunkKey}
            {...props}
            apiBase={API_BASE}
            autoOpen
            {...(props.resetKey != null ? { resetKey: props.resetKey } : {})}
            onMounted={() => {
              props.emit("visual_panel_mount_ms", {
                ms: Math.round(performance.now() - mountStart.current),
                phase: "ready",
              });
            }}
          />
        </Suspense>
      </VisualSearchChunkBoundary>
    </>
  );
}
